import { useState } from 'react';
import { useNavigate } from '@tanstack/react-router';

import { countersApi, type CustomField } from '@/api/queries/counters';
import { Button, Icon, Input, Modal, Textarea, useToast } from '@/components/ui';

const LANG_OPTIONS = ['cs', 'sk', 'en', 'de', 'pl', 'fr'] as const;

interface Props {
  onClose: () => void;
}

export function NewCounterTableModal({ onClose }: Props) {
  const navigate = useNavigate();
  const create = countersApi.useCreateTable();
  const toast = useToast();
  const [name, setName] = useState('');
  const [key, setKey] = useState('');
  const [desc, setDesc] = useState('');
  const [defaultLang, setDefaultLang] = useState('cs');
  const [langs, setLangs] = useState<string[]>(['cs', 'en']);
  const [fields, setFields] = useState<CustomField[]>([]);

  const toggleLang = (lang: string) => {
    setLangs((prev) =>
      prev.includes(lang) ? prev.filter((l) => l !== lang) : [...prev, lang],
    );
  };

  const addField = () => {
    setFields((prev) => [...prev, { key: '', label: '', type: 'string', i18n: false, required: false }]);
  };
  const updateField = (i: number, patch: Partial<CustomField>) => {
    setFields((prev) => prev.map((f, idx) => (idx === i ? { ...f, ...patch } : f)));
  };
  const removeField = (i: number) => {
    setFields((prev) => prev.filter((_, idx) => idx !== i));
  };

  const submit = async () => {
    if (!name || !key) {
      toast.show({ severity: 'error', title: 'Name and key are required' });
      return;
    }
    try {
      const table = await create.mutateAsync({
        name,
        key,
        description: desc || undefined,
        defaultLang,
        langs: langs.includes(defaultLang) ? langs : [...langs, defaultLang],
        customFields: fields.filter((f) => f.key && f.label),
      });
      toast.show({ severity: 'success', title: `Created counter ${table.name}` });
      onClose();
      navigate({ to: '/counters/$key', params: { key: table.key } });
    } catch (err) {
      toast.show({
        severity: 'error',
        title: err instanceof Error ? err.message : 'Failed to create counter',
      });
    }
  };

  return (
    <Modal
      open
      onClose={onClose}
      size="lg"
      title="New counter table"
      subtitle="Create a reference-data table used by mappings, segments, and templates."
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" onClick={submit} loading={create.isPending}>
            Create table
          </Button>
        </>
      }
    >
      <div style={{ display: 'grid', gap: 12 }}>
        <Field label="Name">
          <Input
            value={name}
            placeholder="Cities"
            onChange={(e) => {
              setName(e.currentTarget.value);
              if (!key) setKey(slugify(e.currentTarget.value));
            }}
          />
        </Field>
        <div style={{ display: 'grid', gap: 4 }}>
          <span style={{ fontSize: 12, color: 'var(--text-2)', fontWeight: 500 }}>Key</span>
          <Input
            value={key}
            placeholder="cities or contact.profile.gender"
            onChange={(e) => setKey(e.currentTarget.value.trim())}
          />
          <span style={{ fontSize: 11, color: 'var(--text-3)' }}>
            Lowercase dot-path, used as counter.&lt;key&gt; in mappings. A <code>contact.</code> prefix
            (e.g. <code>contact.profile.gender</code>) makes the table&apos;s values suggestions for that
            contact field in the editor.
          </span>
        </div>
        <Field label="Description">
          <Textarea
            value={desc}
            onChange={(e) => setDesc(e.currentTarget.value)}
            rows={2}
            placeholder="What this counter represents"
          />
        </Field>
        <Field label="Default language">
          <select
            className="ds-input"
            value={defaultLang}
            onChange={(e) => setDefaultLang(e.currentTarget.value)}
          >
            {LANG_OPTIONS.map((l) => (
              <option key={l} value={l}>
                {l}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Languages">
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {LANG_OPTIONS.map((l) => (
              <label
                key={l}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 4,
                  padding: '4px 10px',
                  borderRadius: 6,
                  border: '1px solid var(--border)',
                  background: langs.includes(l) ? 'var(--brand-soft)' : 'transparent',
                  cursor: 'pointer',
                }}
              >
                <input
                  type="checkbox"
                  checked={langs.includes(l)}
                  onChange={() => toggleLang(l)}
                  disabled={l === defaultLang}
                />
                {l}
                {l === defaultLang && <span style={{ color: 'var(--text-3)' }}>· default</span>}
              </label>
            ))}
          </div>
        </Field>

        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
            <strong>Custom fields</strong>
            <Button size="sm" variant="ghost" leftIcon={<Icon name="plus" size={12} />} onClick={addField}>
              Add field
            </Button>
          </div>
          {fields.length === 0 ? (
            <p style={{ fontSize: 13, color: 'var(--text-3)' }}>
              Default fields (key, value, description, parent) are always present. Add your own
              fields here.
            </p>
          ) : (
            <div style={{ display: 'grid', gap: 8 }}>
              {fields.map((f, i) => (
                <div
                  key={i}
                  style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 120px auto auto auto', gap: 6 }}
                >
                  <Input
                    placeholder="key"
                    value={f.key}
                    onChange={(e) => updateField(i, { key: e.currentTarget.value })}
                  />
                  <Input
                    placeholder="Label"
                    value={f.label}
                    onChange={(e) => updateField(i, { label: e.currentTarget.value })}
                  />
                  <select
                    className="ds-input"
                    value={f.type}
                    onChange={(e) => updateField(i, { type: e.currentTarget.value as CustomField['type'] })}
                  >
                    <option value="string">string</option>
                    <option value="number">number</option>
                    <option value="boolean">boolean</option>
                    <option value="date">date</option>
                    <option value="reference">reference</option>
                  </select>
                  <label style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12 }}>
                    <input
                      type="checkbox"
                      checked={f.i18n}
                      onChange={(e) => updateField(i, { i18n: e.currentTarget.checked })}
                    />
                    i18n
                  </label>
                  <label style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12 }}>
                    <input
                      type="checkbox"
                      checked={f.required}
                      onChange={(e) => updateField(i, { required: e.currentTarget.checked })}
                    />
                    required
                  </label>
                  <Button size="sm" variant="ghost" onClick={() => removeField(i)} aria-label="Remove">
                    <Icon name="trash" size={12} />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
}

function Field({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <label style={{ display: 'grid', gap: 4 }}>
      <span style={{ fontSize: 12, color: 'var(--text-2)', fontWeight: 500 }}>{label}</span>
      {children}
      {hint && <span style={{ fontSize: 11, color: 'var(--text-3)' }}>{hint}</span>}
    </label>
  );
}

function slugify(s: string) {
  return s
    .toLowerCase()
    .replace(/[^a-z0-9_.]+/g, '_')
    .replace(/^[_.]+|[_.]+$/g, '')
    .replace(/^([0-9])/, '_$1');
}
